import React, { useEffect, useRef, useState, lazy, Suspense, useMemo } from "react";
import { Container, Nav, Navbar, Modal, Button, NavDropdown } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import "./index.css";
import { Link } from "react-router-dom";
import { fetchHotelData, getHotelDetails } from "../../Utils/index"; 
const UserWayAccessibilityMenu = lazy(() => import("../UserWayAccessibilityMenu/index.js"));

const HeaderNavbar = () => {
    const pathName = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const isLaunchPartyPage = (pathName === "/launchparty" || pathName === "/launchparty/");
    const isNFTPage = searchParams.has('nft');
    const imgURL = process.env.REACT_APP_CDN_PATH + "hotel/flordeindia/images/homepage/";
    const preloadUserWayAccessibilityMenu = () => import("../UserWayAccessibilityMenu");
    
    const roomItems = [
        { path: "/rooms/amara", label: "AMARA" },
        { path: "/rooms/elyunque", label: "EL YUNQUE" },
        { path: "/rooms/coqui", label: "COQUÍ" },
        { path: "/rooms/taino", label: "TAÍNO" },
        { path: "/rooms/coco", label: "COCO" },
        { path: "/rooms/flamenco", label: "FLAMENCO" },
        { path: "/rooms/borinquen", label: "BORINQUEN" },
        { path: "/rooms/nirvana", label: "NIRVANA" },
        { path: "/rooms/playa", label: "PLAYA" },
        { path: "/rooms/encanto", label: "ENCANTO" },
    ];

    // State management
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [activeRoute, setActiveRoute] = useState("/");
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [userWayPreloaded, setUserWayPreloaded] = useState(false);
    const [thirdPartyBooking, setThirdPartyBooking] = useState(true); // Default to true

    const isMobileOrTabletDevice = useMemo(() => {
        const width = windowWidth;
        const height = window.innerHeight;
        const isMobile = width <= 768 && height <= 1024;
        const isTablet = width > 768 && width <= 991;
        return isMobile || isTablet;
    }, [windowWidth]);

    // Fetch hotel data and check thirdPartyBooking setting
    useEffect(() => {
        const loadHotelConfig = async () => {
            try {
                const hotelData = await fetchHotelData();
                const hotelDetails = getHotelDetails(hotelData);
                
                // Check if thirdPartyBooking exists in the API response
                if (hotelDetails?.details?.thirdPartyBooking !== undefined) {
                    setThirdPartyBooking(hotelDetails.details.thirdPartyBooking);
                } else if (hotelData?.data?.hotel?.details?.thirdPartyBooking !== undefined) {
                    setThirdPartyBooking(hotelData.data.hotel.details.thirdPartyBooking);
                }
            } catch (error) {
                console.error("Error loading hotel configuration:", error);
                // Fallback to environment variable or default
                setThirdPartyBooking(true);
            }
        };

        loadHotelConfig();
    }, []);

    // Preload ADA icon on mount (but keep hidden until dropdown is open)
    useEffect(() => {
        preloadUserWayAccessibilityMenu();
        setUserWayPreloaded(true);
    }, []);

    // Refs
    const navbarRef = useRef(null);
    const togglerRef = useRef(null);

    // Hooks
    const navigate = useNavigate();
    const location = useLocation();

    // Handle scroll effect and auto-close mobile menu
    useEffect(() => {
        const handleScroll = () => {
            // Handle navbar background change on scroll
            if (window.scrollY > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }

            // Auto-close mobile menu on scroll
            if (isMenuOpen && window.innerWidth < 992) {
                setIsMenuOpen(false);
            }
        };

        window.addEventListener("scroll", handleScroll);

        // Cleanup
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [isMenuOpen]); // Added isMenuOpen as dependency

    // Fix for navbar flickering when modal opens
    useEffect(() => {
        if (showBookingModal || showSuccessModal) {
            // Calculate scrollbar width
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

            // Add padding to navbar to compensate for scrollbar
            if (navbarRef.current) {
                navbarRef.current.style.paddingRight = `${scrollbarWidth}px`;
            }
            document.body.style.paddingRight = `${scrollbarWidth}px`;
            document.body.style.overflow = 'hidden';
        } else {
            // Reset styles when modal closes
            if (navbarRef.current) {
                navbarRef.current.style.paddingRight = '';
            }
            document.body.style.paddingRight = '';
            document.body.style.overflow = '';
        }

        // Cleanup function
        return () => {
            if (navbarRef.current) {
                navbarRef.current.style.paddingRight = '';
            }
            document.body.style.paddingRight = '';
            document.body.style.overflow = '';
        };
    }, [showBookingModal, showSuccessModal]);

    // Update active route based on location
    useEffect(() => {
        setActiveRoute(location.pathname);
    }, [location]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth); // Update window width state
            if (window.innerWidth >= 992 && isMenuOpen) {
                setIsMenuOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [isMenuOpen]);

    // Handle menu toggle animation
    useEffect(() => {
        if (togglerRef.current) {
            if (isMenuOpen) {
                togglerRef.current.classList.add("is-active");
            } else {
                togglerRef.current.classList.remove("is-active");
            }
        }
    }, [isMenuOpen]);

    // Navigation handler
    const handleNavigation = (route) => {
        navigate(route);
        setActiveRoute(route);

        // Close mobile menu after navigation
        if (window.innerWidth < 992) {
            setIsMenuOpen(false);
        }
    };

    const handleToggle = (expanded) => {
        // Prevent rapid toggling
        setTimeout(() => {
            setIsMenuOpen(expanded);
        }, 50);
    };

    // Handle Book Now button click
    const handleBookNowClick = (e) => {
        e.preventDefault();
        
        // Close mobile menu if open
        if (window.innerWidth < 992) {
            setIsMenuOpen(false);
        }
        
        if (thirdPartyBooking) {
            // Redirect to third-party booking URL
            const bookingURL = process.env.REACT_APP_BOOKING_URL || 'https://flordeindia.holidayfuture.com/';
            window.open(bookingURL, '_blank', 'noopener,noreferrer');
        } else {
            // Navigate to internal booking page
            navigate('/booking');
        }
    };

    // Handle modal close
    const handleCloseBookingModal = () => {
        setShowBookingModal(false);
    };

    // Handle form submission success
    const handleFormSuccess = () => {
        setShowBookingModal(false);
        // Show success modal after a brief delay for smooth transition
        setTimeout(() => {
            setShowSuccessModal(true);
        }, 300);
    };

    // Handle success modal close
    const handleSuccessModalClose = () => {
        setShowSuccessModal(false);
    };

    // Navigation items configuration
    const navItems = [
        { path: "/", label: "HOME" },
        { path: "/rooms", label: "ROOMS", isDropdown: true },
        { path: "/property-highlights", label: "Property Highlights" },
        { path: "/guide-to-the-island", label: "GUIDE TO THE ISLAND" },
        { path: "/exclusive-offers", label: "EXCLUSIVE OFFERS" },
        { path: "/faq", label: "FAQS" },
        { path: "/contactus", label: "CONTACT" }
    ];

    // Determine if desktop accessibility menu should be shown
    const showDesktopAccessibility = useMemo(() => {
        return windowWidth >= 992; // d-lg-block breakpoint
    }, [windowWidth]);

    return (
        <>
            <Suspense fallback={null}>
            </Suspense>
            <Navbar
                ref={navbarRef}
                expand="lg"
                fixed="top"
                className={`custom-header ${isScrolled ? "scrolled" : ""}`}
                expanded={isMenuOpen}
                onToggle={handleToggle}
            >
                <Container>
                    <Navbar.Brand
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleNavigation("/");
                        }}
                    >
                        <img
                            src={imgURL + "navbar-logo-new.webp"}
                            alt="Flor De India"
                            className="logo-img"
                        />
                    </Navbar.Brand>

                    {(isLaunchPartyPage || isNFTPage) ?
                        <>
                            {/* Navigation Items */}
                            <Navbar.Collapse id="navbar-nav" className="newchangesnavbardektop">
                                <Nav className="mx-auto">
                                    <div className="mobile-AccessibilityMenu-show">
                                        <UserWayAccessibilityMenu color="white" width={40} height={40} />
                                    </div>
                                </Nav>

                                {/* Book Now Button - Hide on NFT page */}
                                {!isNFTPage && (
                                    <div className="d-flex justify-content-center mt-2">
                                        <button
                                            className="btn btn-book d-block"
                                            onClick={() => {
                                                if (window.innerWidth < 992) {
                                                    setIsMenuOpen(false);
                                                }
                                                // Try multiple selectors to find the form section
                                                const formSection = document.getElementById('form-section') || 
                                                                  document.querySelector('.form-section') || 
                                                                  document.querySelector('[class*="form"]') || 
                                                                  document.querySelector('[id*="form"]');
                                                
                                                if (formSection) {
                                                    const elementPosition = formSection.getBoundingClientRect().top + window.pageYOffset;
                                                    window.scrollTo({ top: elementPosition - 60, behavior: 'smooth' });
                                                } else {
                                                    // Fallback: scroll to a reasonable position down the page
                                                    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
                                                }
                                            }}
                                        >
                                            Register Now
                                        </button>
                                    </div>
                                )}
                            </Navbar.Collapse>

                            <div id="navbar-nav" className="newchangesnavbarmobile">
                                <Nav className="mx-auto">
                                    <div className="mobile-AccessibilityMenu-show">
                                        <UserWayAccessibilityMenu color="white" width={40} height={40} />
                                    </div>
                                </Nav>

                                {/* Book Now Button - Hide on NFT page */}
                                {!isNFTPage && (
                                    <div className="d-flex justify-content-center mt-2">
                                        <button
                                            className="btn btn-book d-block"
                                            onClick={() => {
                                                if (window.innerWidth < 992) {
                                                    setIsMenuOpen(false);
                                                }
                                                // Try multiple selectors to find the form section
                                                const formSection = document.getElementById('form-section') || 
                                                                  document.querySelector('.form-section') || 
                                                                  document.querySelector('[class*="form"]') || 
                                                                  document.querySelector('[id*="form"]');
                                                
                                                if (formSection) {
                                                    const elementPosition = formSection.getBoundingClientRect().top + window.pageYOffset;
                                                    window.scrollTo({ top: elementPosition - 60, behavior: 'smooth' });
                                                } else {
                                                    // Fallback: scroll to a reasonable position down the page
                                                    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
                                                }
                                            }}
                                        >
                                            Register Now
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    :
                        <>
                            {/* Custom Toggle Button */}
                            <Navbar.Toggle
                                aria-controls="navbar-nav"
                                className="custom-toggler"
                                ref={togglerRef}
                            >
                                <span className="hamburger">
                                    <span className="line line-1"></span>
                                    <span className="line line-2"></span>
                                    <span className="line line-3"></span>
                                </span>
                            </Navbar.Toggle>

                            {/* Navigation Items */}
                            <Navbar.Collapse id="navbar-nav">
                                <Nav className="mx-auto">
                                    {navItems.map((item) => (
                                        item.isDropdown ? (
                                            <NavDropdown
                                                key={item.path}
                                                title={
                                                    <span className="dropdown-title d-flex align-items-center" onClick={() => setShowDropdown(!showDropdown)}>
                                                        {item.label}
                                                        <span className="dropdown-icon ms-2"></span>
                                                    </span>
                                                }
                                                id="rooms-dropdown"
                                                menuVariant="dark"
                                                className="custom-dropdown"
                                                show={showDropdown}
                                                onMouseEnter={() => setShowDropdown(true)}
                                                onMouseLeave={() => setShowDropdown(false)}
                                            >
                                                {roomItems.map((room) => (
                                                    <NavDropdown.Item
                                                        as={Link}
                                                        to={room.path}
                                                        key={room.path}
                                                        className="custom-dropdown-item"
                                                        onClick={() => {
                                                            setShowDropdown(false);
                                                            if (window.innerWidth < 992) {
                                                                setIsMenuOpen(false);
                                                            }
                                                        }}
                                                    >
                                                        {room.label}
                                                    </NavDropdown.Item>
                                                ))}
                                            </NavDropdown>
                                        ) : (
                                            <Nav.Link
                                                key={item.path}
                                                className={activeRoute === item.path ? "active" : ""}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleNavigation(item.path);
                                                }}
                                            >
                                                {item.label}
                                            </Nav.Link>
                                        )
                                    ))}
                                    <div className="mobile-AccessibilityMenu-show">
                                        <UserWayAccessibilityMenu color="white" width={40} height={40} />
                                    </div>
                                </Nav>

                                {/* Book Now Button */}
                                <div className="d-lg-flex">
                                    <a
                                        href={thirdPartyBooking ? (process.env.REACT_APP_BOOKING_URL || 'https://flordeindia.holidayfuture.com/') : '/booking'}
                                        className="btn btn-book"
                                        target={thirdPartyBooking ? "_blank" : "_self"}
                                        rel={thirdPartyBooking ? "noopener noreferrer" : undefined}
                                        onClick={(e) => {
                                            if (window.innerWidth < 992) {
                                                setIsMenuOpen(false);
                                            }
                                            if (!thirdPartyBooking) {
                                                handleBookNowClick(e);
                                            }
                                        }}
                                    >
                                        Book Now
                                    </a>
                                </div>
                            </Navbar.Collapse>
                        </>
                    }

                    {/* Desktop Accessibility Menu - Only show on desktop with dynamic visibility */}
                    {showDesktopAccessibility && (
                        <div className="desktopaccesibility">
                            <Suspense fallback={null}>
                                <UserWayAccessibilityMenu color="white" width={40} height={40} />
                            </Suspense>
                        </div>
                    )}
                </Container>
            </Navbar>
        </>
    );
};

export default HeaderNavbar;